/**
 * Cadastro Unik · Página cinematográfica de onboarding e briefing estratégico.
 * Fonte: Perguntas oficiais ClickUp (ERPigs) · Padrão visual Genesis Lux (Lara Lívia).
 */
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { api, ApiClientError } from "../lib/api";
import { useSession } from "../lib/session";
import { BrandMark } from "../ui/BrandMark";
import { Button } from "../ui/Button";
import { Input, TextArea } from "../ui/Input";
import { FloatingWhatsApp, WhatsAppIcon, UNIK_WHATSAPP_PHONE } from "../ui/FloatingWhatsApp";

const DRAFT_KEY = "unik_cadastro_draft_v2";
const CHAPTER_KEY = "unik_cadastro_chapter_v2";
const SUBMITTED_KEY = "unik_cadastro_submitted_v2";

interface InviteData {
  url: string;
  message: string;
  whatsappUrl: string;
  useCount: number;
  lastUsedAt: string | null;
}

interface FormState {
  name: string;
  email: string;
  whatsapp: string;
  instagram: string;
  password: string;
  responses: Record<string, string>;
}

const INITIAL_STATE: FormState = {
  name: "",
  email: "",
  whatsapp: "",
  instagram: "",
  password: "",
  responses: {},
};

interface Chapter {
  id: number;
  title: string;
  subtitle: string;
  fields: {
    key: string;
    label: string;
    hint?: string;
    placeholder: string;
    required?: boolean;
    rows?: number;
  }[];
}

const CHAPTERS: Chapter[] = [
  {
    id: 1,
    title: "Identificação e Acesso",
    subtitle: "Seus dados de contato e criação de acesso à plataforma Unik.",
    fields: [
      {
        key: "name",
        label: "Qual seu nome completo?",
        placeholder: "Exemplo: Luana Campi",
        required: true,
      },
      {
        key: "instagram",
        label: "Qual o @ do seu Instagram?",
        hint: "O perfil comercial que vamos posicionar estrategicamente.",
        placeholder: "@seuperfil",
        required: true,
      },
      {
        key: "email",
        label: "Qual seu melhor e-mail?",
        placeholder: "contato@suamarca.com.br",
        required: true,
      },
      {
        key: "whatsapp",
        label: "Qual seu WhatsApp?",
        hint: "Com DDD para alinhamentos rápidos com nossa equipe.",
        placeholder: "(11) 99999-9999",
        required: true,
      },
      {
        key: "password",
        label: "Crie uma senha de acesso",
        hint: "Mínimo de 8 caracteres para entrar na sua área de cliente.",
        placeholder: "Crie uma senha segura",
        required: false,
      },
    ],
  },
  {
    id: 2,
    title: "Posicionamento e Diferenciação",
    subtitle: "Como você se destaca e como sua marca deve ser percebida.",
    fields: [
      {
        key: "sala_concorrentes",
        label: "Se sua marca fosse uma pessoa, como ela chegaria numa sala cheia de concorrentes?",
        placeholder: "Descreva a postura, a presença, a energia e a impressão que ela causaria ao entrar no ambiente.",
        rows: 4,
        required: true,
      },
      {
        key: "faz_melhor_90",
        label: "O que você faz melhor que 90% do seu mercado, mesmo que ninguém ainda saiba?",
        placeholder: "Aquele detalhe técnico, cuidado com o cliente, velocidade ou rigor que seus concorrentes ignoram.",
        rows: 4,
        required: true,
      },
      {
        key: "nao_quer_parecer",
        label: "O que você não quer parecer nas redes sociais de jeito nenhum?",
        placeholder: "Exemplos: amadora, agressiva em vendas, superficial, arrogante, engessada...",
        rows: 3,
        required: true,
      },
      {
        key: "por_que_compram",
        label: "Por que as pessoas compram de você? Qual é o seu verdadeiro diferencial?",
        placeholder: "O motivo real que faz um cliente fechar contrato com você e indicar seus serviços.",
        rows: 4,
        required: true,
      },
      {
        key: "escolha_mais_caro",
        label: "O que faria alguém te escolher mesmo sendo a opção mais cara?",
        placeholder: "Qual certeza, exclusividade ou segurança você entrega que justifica um ticket mais alto?",
        rows: 4,
        required: true,
      },
    ],
  },
  {
    id: 3,
    title: "Missão, Crenças e História",
    subtitle: "A alma da sua marca, valores inegociáveis e pontos de vista fortes.",
    fields: [
      {
        key: "missao_marca",
        label: "Qual é a missão da sua marca? O que ela defende com unhas e dentes?",
        placeholder: "A causa, a transformação ou o princípio ético que move seu trabalho todos os dias.",
        rows: 4,
        required: true,
      },
      {
        key: "mantra",
        label: "Se sua marca tivesse um mantra oficial, qual seria?",
        placeholder: "Uma frase marcante, que resuma sua filosofia de trabalho e vida.",
        rows: 3,
        required: true,
      },
      {
        key: "historia_marca",
        label: "Qual é a história de criação da sua marca?",
        placeholder: "Como tudo começou, os momentos difíceis, as viradas de chave e o propósito que nasceu dessa trajetória.",
        rows: 5,
        required: true,
      },
      {
        key: "opiniao_polemica",
        label: "Qual opinião sua pode gerar identificação forte ou leve polêmica no seu mercado?",
        placeholder: "Um ponto fora da curva, algo que todo mundo faz mas você discorda veementemente.",
        rows: 4,
        required: true,
      },
      {
        key: "pensar_30_dias",
        label: "Se alguém lesse seu conteúdo por 30 dias seguidos, em que ela precisaria mudar a forma de pensar?",
        placeholder: "Qual crença limitante ou mito ela deixaria para trás após acompanhar você?",
        rows: 4,
        required: true,
      },
    ],
  },
  {
    id: 4,
    title: "Conexão e Percepção no Instagram",
    subtitle: "A estratégia visual, intenção de cada post e expectativas para seu perfil.",
    fields: [
      {
        key: "missao_instagram",
        label: "Se o seu Instagram tivesse uma única missão, qual seria?",
        placeholder: "Exemplo: educar o público para vender tratamentos premium sem ter que dar descontos.",
        rows: 3,
        required: true,
      },
      {
        key: "primeira_impressao",
        label: "O que você quer que as pessoas pensem ao entrar no seu perfil pela primeira vez?",
        placeholder: "A sensação imediata: sofisticação, autoridade médica, clareza, acolhimento, solidez...",
        rows: 3,
        required: true,
      },
      {
        key: "mudanca_perfil",
        label: "O que você espera de mudança no seu perfil nos próximos meses?",
        placeholder: "Mais consultas agendadas, aumento no valor percebido, seguidores mais qualificados...",
        rows: 4,
        required: true,
      },
      {
        key: "orgulho_stories",
        label: "Qual tipo de post te daria orgulho genuíno de compartilhar nos stories?",
        placeholder: "Um carrossel com ensinamento profundo, um resultado real de cliente, uma análise de caso...",
        rows: 4,
        required: true,
      },
      {
        key: "salvar_post",
        label: "O que faria uma pessoa salvar um post seu e pensar: “vou ler isso com calma depois”?",
        placeholder: "Que tipo de orientação prática, guia ou revelação tem esse poder no seu nicho?",
        rows: 4,
        required: true,
      },
    ],
  },
  {
    id: 5,
    title: "A Mente do seu Cliente",
    subtitle: "Dores, necessidades e desejos do público que você quer atrair.",
    fields: [
      {
        key: "tipo_cliente",
        label: "Que tipo de cliente você quer atrair? (Foque no comportamento, não no bolso)",
        placeholder: "Pessoas decididas, que valorizam qualidade, respeitam o profissional e seguem as recomendações.",
        rows: 4,
        required: true,
      },
      {
        key: "desejos_publico",
        label: "Quais são os principais desejos do seu público?",
        hint: "Exemplos: buscam confiança, pertencimento, tranquilidade, status, rejuvenescimento...",
        placeholder: "O que tira o fôlego deles positivamente quando alcançam o resultado?",
        rows: 4,
        required: true,
      },
      {
        key: "necessidades_publico",
        label: "Quais são as necessidades reais do seu público?",
        hint: "Exemplos: praticidade, segurança, atendimento humanizado, clareza nas orientações...",
        placeholder: "O que eles precisam sentir ou receber no processo de atendimento?",
        rows: 4,
        required: true,
      },
      {
        key: "dores_publico",
        label: "Quais são as maiores dores e medos do seu público?",
        hint: "Exemplos: insegurança, frustração com tratamentos anteriores, medo de errar na escolha...",
        placeholder: "O que tira o sono desse cliente antes de encontrar a sua solução?",
        rows: 4,
        required: true,
      },
      {
        key: "objetivo_central",
        label: "Qual o objetivo central de longo prazo para a sua marca no digital?",
        placeholder: "Ser a principal referência na sua cidade, expandir para novas filiais, lançar mentorias...",
        rows: 4,
        required: true,
      },
    ],
  },
];


function buildBriefingWhatsAppMessage(formData: FormState): string {
  const lines: string[] = [];
  lines.push("📋 *NOVO BRIEFING PREENCHIDO - UNIK*");
  lines.push("══════════════════════════");
  lines.push(`👤 *Cliente:* ${formData.name || "Não informado"}`);
  if (formData.instagram) lines.push(`📸 *Instagram:* ${formData.instagram}`);
  if (formData.whatsapp) lines.push(`📱 *WhatsApp:* ${formData.whatsapp}`);
  if (formData.email) lines.push(`✉️ *E-mail:* ${formData.email}`);
  lines.push("");
  lines.push("✨ *RESUMO DAS RESPOSTAS DO BRIEFING:*");

  CHAPTERS.forEach((ch) => {
    if (ch.id === 1) return; // Identificação já detalhada acima
    const chapterFields = ch.fields
      .map((f) => {
        const val = formData.responses[f.key]?.trim();
        if (!val) return null;
        return `• *${f.label}*\n  ${val}`;
      })
      .filter(Boolean);

    if (chapterFields.length > 0) {
      lines.push("");
      lines.push(`📌 *CAPÍTULO ${ch.id}: ${ch.title.toUpperCase()}*`);
      chapterFields.forEach((item) => lines.push(item!));
    }
  });

  lines.push("");
  lines.push("══════════════════════════");
  lines.push("🔗 *Acessar na Gestão Unik:* https://falaunik.com/gestao");

  let fullMsg = lines.join("\n");
  // Se ultrapassar o limite seguro de URL do WhatsApp (~3500 chars), cria versão condensada
  if (fullMsg.length > 3500) {
    const compact: string[] = [];
    compact.push("📋 *NOVO BRIEFING PREENCHIDO - UNIK*");
    compact.push("══════════════════════════");
    compact.push(`👤 *Cliente:* ${formData.name || "Não informado"}`);
    if (formData.instagram) compact.push(`📸 *Instagram:* ${formData.instagram}`);
    if (formData.whatsapp) compact.push(`📱 *WhatsApp:* ${formData.whatsapp}`);
    if (formData.email) compact.push(`✉️ *E-mail:* ${formData.email}`);
    compact.push("");
    compact.push("✨ *PRINCIPAIS DESTAQUES:*");
    
    // Pega as perguntas mais críticas de posicionamento
    const keyHighlights = [
      "sala_concorrentes",
      "faz_melhor_90",
      "nao_quer_parecer",
      "por_que_compram",
      "posicionamento_desejado",
      "dores_publico",
      "objetivo_central"
    ];
    
    keyHighlights.forEach((k) => {
      const val = formData.responses[k]?.trim();
      if (val) {
        compact.push(`• *${k.replace(/_/g, " ").toUpperCase()}:* ${val.slice(0, 200)}`);
      }
    });

    compact.push("");
    compact.push("══════════════════════════");
    compact.push("🔗 *Acessar Respostas Completas:* https://falaunik.com/gestao");
    fullMsg = compact.join("\n");
  }

  return fullMsg;
}

export function Cadastro() {
  const navigate = useNavigate();
  const { session } = useSession();
  const isAdmin = session?.actorType === "user";

  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [currentChapter, setCurrentChapter] = useState<number>(() => {
    try {
      const savedCh = localStorage.getItem(CHAPTER_KEY);
      if (savedCh) {
        const parsed = parseInt(savedCh, 10);
        if (parsed >= 1 && parsed <= 5) return parsed;
      }
    } catch {}
    return 1;
  });

  const [formData, setFormData] = useState<FormState>(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) return { ...INITIAL_STATE, ...JSON.parse(saved) };
    } catch {}
    return INITIAL_STATE;
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(() => {
    try {
      return localStorage.getItem(SUBMITTED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Admin Tools (se logado como equipe)
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api
        .get<InviteData | null>("/invites/current")
        .then(setInvite)
        .catch(() => undefined);
    }
  }, [isAdmin]);

  // Salva rascunho em localStorage em tempo real (permanece mesmo após envio)
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    } catch {}
  }, [formData]);

  // Salva o capítulo atual em localStorage para o lead retornar exatamente onde parou
  useEffect(() => {
    try {
      localStorage.setItem(CHAPTER_KEY, String(currentChapter));
    } catch {}
  }, [currentChapter]);

  // Fecha o modal de confirmação no Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isClearModalOpen) {
        setIsClearModalOpen(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isClearModalOpen]);

  function updateRootField(field: keyof FormState, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  function updateResponse(key: string, value: string) {
    setFormData((prev) => ({
      ...prev,
      responses: { ...prev.responses, [key]: value },
    }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function formatWhatsapp(val: string) {
    const numbers = val.replace(/\D/g, "").slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  }

  function formatInstagram(val: string) {
    const trimmed = val.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
  }

  function validateChapter(chIndex: number): boolean {
    const chapter = CHAPTERS[chIndex - 1];
    if (!chapter) return true;

    const errors: Record<string, string> = {};

    if (chIndex === 1) {
      if (!formData.name.trim()) errors.name = "Informe seu nome completo.";
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errors.email = "Informe um e-mail válido.";
      }
      if (!formData.whatsapp.trim() || formData.whatsapp.replace(/\D/g, "").length < 10) {
        errors.whatsapp = "Informe um WhatsApp com DDD válido.";
      }
      if (!formData.instagram.trim() || formData.instagram === "@") {
        errors.instagram = "Informe o @ do seu Instagram.";
      }
      if (formData.password && formData.password.length < 8) {
        errors.password = "A senha deve conter no mínimo 8 caracteres.";
      }
    } else {
      for (const field of chapter.fields) {
        if (field.required) {
          const val = formData.responses[field.key]?.trim() || "";
          if (!val) {
            errors[field.key] = "Por favor, responda a este ponto com seus detalhes.";
          }
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleNext() {
    if (validateChapter(currentChapter)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setCurrentChapter((c) => Math.min(CHAPTERS.length, c + 1));
    }
  }

  function handlePrev() {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentChapter((c) => Math.max(1, c - 1));
  }

  function handleConfirmClearDraft() {
    try {
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(CHAPTER_KEY);
      localStorage.removeItem(SUBMITTED_KEY);
      localStorage.removeItem("unik_cadastro_submitted_at");
    } catch {}
    setFormData(INITIAL_STATE);
    setCurrentChapter(1);
    setFieldErrors({});
    setSubmitted(false);
    setIsClearModalOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validateChapter(currentChapter)) return;

    setSubmitting(true);
    setGeneralError(null);

    try {
      await api.post("/cadastro", {
        name: formData.name.trim(),
        email: formData.email.trim(),
        whatsapp: formData.whatsapp.trim(),
        instagram: formatInstagram(formData.instagram),
        password: formData.password || undefined,
        responses: formData.responses,
      });

      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
        localStorage.setItem(SUBMITTED_KEY, "true");
        localStorage.setItem("unik_cadastro_submitted_at", new Date().toISOString());
      } catch {}
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setGeneralError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setGeneralError("Não foi possível enviar suas respostas. Verifique sua conexão e tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function copyInviteLink() {
    if (!invite) return;
    await navigator.clipboard.writeText(invite.url).catch(() => undefined);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  }

  // Estatísticas de preenchimento
  const allRequiredKeys = CHAPTERS.flatMap((ch) =>
    ch.id === 1
      ? ["name", "email", "whatsapp", "instagram"]
      : ch.fields.filter((f) => f.required).map((f) => f.key)
  );

  const filledCount = allRequiredKeys.filter((k) => {
    if (k === "name") return !!formData.name.trim();
    if (k === "email") return !!formData.email.trim();
    if (k === "whatsapp") return !!formData.whatsapp.trim();
    if (k === "instagram") return !!formData.instagram.trim();
    return !!formData.responses[k]?.trim();
  }).length;

  const progressPercent = Math.round((filledCount / allRequiredKeys.length) * 100);

  // Tela de Sucesso
  if (submitted) {
    const firstName = formData.name.split(" ")[0] || "Cliente";
    return (
      <div className="app-shell flex min-h-dvh flex-col items-center justify-center px-6 py-16 text-center">
        <div className="relative mx-auto w-full max-w-lg">
          <div className="liquid-glass rounded-[var(--radius-control-lg)] p-8 sm:p-12">
            <BrandMark variant="creme" className="mx-auto h-8 w-auto" />
            <p className="type-overline mt-6 text-gold">Briefing Social Media Recebido</p>
            <h1
              className="type-h1 mt-3 text-text-cream"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Recebido com sucesso, {firstName}!
            </h1>
            <p className="type-body mt-4 text-text-secondary leading-relaxed">
              Obrigada pela dedicação em cada resposta. Isso aqui é a base de tudo: dos roteiros aos carrosséis, da copy ao posicionamento que realmente converte.
            </p>

            <div className="mt-8 rounded-[var(--radius-control)] border border-border-subtle bg-surface-sunken/60 p-5 text-left">
              <p className="type-caption text-text-tertiary">Próximos passos:</p>
              <p className="type-body-sm mt-1 text-text-secondary">
                1. Nossa estrategista fará a leitura diagnóstica de todas as suas respostas.
              </p>
              <p className="type-body-sm mt-1 text-text-secondary">
                2. Entraremos em contato pelo seu WhatsApp cadastrado ({formData.whatsapp}).
              </p>
            </div>

            <p
              className="mt-8 text-center text-[22px] italic text-text-cream"
              style={{ fontFamily: "var(--font-display)" }}
            >
              XOXO, EQUIPE IGS! S2
            </p>

            {/* Hero CTA: Enviar Respostas no WhatsApp da Lara */}
            <div className="mt-8 rounded-[var(--radius-control)] border border-emerald-500/30 bg-emerald-950/20 p-5 text-center shadow-lg">
              <span className="type-overline text-emerald-400">Entrega de Respostas</span>
              <h3 className="type-h3 mt-1 text-text-cream font-medium">
                Enviar Briefing para a Lara
              </h3>
              <p className="type-body-sm mt-1.5 text-text-secondary">
                Toque no botão abaixo para abrir o WhatsApp oficial com o resumo completo do seu briefing preenchido:
              </p>

              <a
                href={`https://wa.me/${UNIK_WHATSAPP_PHONE}?text=${encodeURIComponent(buildBriefingWhatsAppMessage(formData))}`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] px-6 py-4 text-base font-semibold text-white shadow-[0_8px_25px_rgba(0,0,0,0.5),0_0_25px_rgba(37,211,102,0.4)] hover:bg-[#20ba59] hover:shadow-[0_10px_35px_rgba(0,0,0,0.6),0_0_35px_rgba(37,211,102,0.6)] hover:scale-[1.02] active:scale-95 transition-all duration-300"
              >
                <WhatsAppIcon className="h-6 w-6 text-white shrink-0 drop-shadow" />
                <span>Enviar Briefing no WhatsApp da Lara</span>
              </a>
              <p className="type-caption mt-2.5 text-text-tertiary">
                Destino: +55 31 99537-4661 · Atendimento oficial Unik
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              {formData.password ? (
                <Button size="lg" shape="invite" onClick={() => navigate("/aprovacoes")}>
                  Acessar Minha Área Unik
                </Button>
              ) : (
                <Button size="lg" shape="invite" onClick={() => navigate("/entrar-cliente")}>
                  Ir para Login
                </Button>
              )}

              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-xs font-medium text-text-secondary hover:text-text-cream liquid-glass-frosted border border-white/10 hover:border-gold/30 active:scale-95 transition-all"
              >
                <span>✏️</span>
                <span>Revisar ou editar respostas enviadas</span>
              </button>
            </div>
          </div>
        </div>
        <FloatingWhatsApp phone={UNIK_WHATSAPP_PHONE} message={buildBriefingWhatsAppMessage(formData)} label="Enviar no WhatsApp" />
      </div>
    );
  }

  const activeChapter = CHAPTERS[currentChapter - 1] ?? CHAPTERS[0]!;

  return (
    <div className="app-shell min-h-dvh">
      {/* Barra de Progresso Fina no Topo */}
      <div className="fixed left-0 right-0 top-0 z-[var(--z-sticky)] h-[3px] bg-border-subtle">
        <div
          className="h-full bg-gold transition-all duration-300 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Painel Administrativo Discreto (Apenas se Logado como Equipe) */}
      {isAdmin && (
        <aside aria-label="Controles administrativos" className="border-b border-border-subtle bg-surface-raised/90 px-4 py-2 text-xs">
          <div className="mx-auto flex max-w-3xl items-center justify-between">
            <span className="type-caption text-gold font-medium">Modo Equipe Ativo</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                className="type-caption text-text-secondary hover:text-text-cream underline"
              >
                {showAdminPanel ? "Ocultar ferramentas de link" : "Ferramentas de convite"}
              </button>
              <Link to="/gestao" className="type-caption text-text-secondary hover:text-text-cream">
                Ir para Gestão →
              </Link>
            </div>
          </div>
          {showAdminPanel && invite && (
            <div className="mx-auto mt-2 max-w-3xl rounded border border-border-subtle bg-surface-sunken p-3">
              <p className="type-caption text-text-secondary">
                Link permanente de cadastro: <span className="text-text-cream">{invite.url}</span>
              </p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="secondary" onClick={copyInviteLink}>
                  {linkCopied ? "Copiado!" : "Copiar Link"}
                </Button>
                <a href={invite.whatsappUrl} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="ghost">Abrir no WhatsApp</Button>
                </a>
              </div>
            </div>
          )}
        </aside>
      )}

      {/* Cabeçalho Editorial com Textura Genesis Lux */}
      <header className="ambient-glow grain relative overflow-hidden border-b border-border-subtle px-6 py-14 sm:py-20 text-center">
        <img
          src="/brand/simbolo-creme.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-28 top-1/2 h-[160%] w-auto -translate-y-1/2 opacity-[0.04]"
        />
        <div className="relative mx-auto w-full max-w-2xl">
          <BrandMark variant="creme" className="mx-auto h-7 w-auto" />
          <p className="type-overline mt-4 text-gold tracking-[0.2em]">
            Briefing Oficial Social Media
          </p>
          <h1
            className="type-display mt-4 text-text-cream"
            style={{ fontFamily: "var(--font-display)", fontSize: "clamp(32px, 5vw, 56px)" }}
          >
            Presença Digital com Intenção
          </h1>
          <p className="type-body mt-4 text-text-secondary leading-relaxed max-w-xl mx-auto">
            Senta aqui, vamos conversar rapidinho: não é apenas ter um feed bonito. É ter um feed que convence.
            Este diagnóstico é a base dos seus roteiros, carrosséis e do posicionamento que transforma seguidores em clientes fiéis.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {/* Pílula: Respondidos */}
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium text-text-secondary liquid-glass-frosted border border-white/10 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-gold"></span>
              <span>
                <strong className="text-text-cream font-semibold">{filledCount}</strong> de {allRequiredKeys.length} respondidos
              </span>
            </div>

            {/* Pílula: Progresso */}
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium text-text-secondary liquid-glass-frosted border border-white/10 shadow-sm">
              <span className="text-gold">✦</span>
              <span>
                <strong className="text-text-cream font-semibold">{progressPercent}%</strong> completo
              </span>
            </div>

            {/* Pílula Interativa: Limpar Rascunho */}
            <button
              type="button"
              onClick={() => setIsClearModalOpen(true)}
              title="Limpar todos os campos e recomeçar o formulário"
              className="group inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium text-text-tertiary hover:text-danger border border-white/10 hover:border-danger/40 liquid-glass-frosted hover:bg-danger/10 transition-all duration-300 active:scale-95 shadow-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 group-hover:text-danger transition-all"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              <span>Limpar rascunho</span>
            </button>
          </div>
        </div>
      </header>

      {/* Navegação entre Capítulos */}
      <nav aria-label="Capítulos do formulário" className="sticky top-0 z-[var(--z-sticky)] border-b border-white/[0.08] bg-surface-base/80 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
        <div className="relative mx-auto max-w-4xl px-4 py-3">
          <div className="mask-fade-x overflow-x-auto no-scrollbar scroll-smooth flex items-center justify-start sm:justify-center gap-2.5 py-1 px-2">
            {CHAPTERS.map((ch) => {
              const isActive = currentChapter === ch.id;
              const isPassed = currentChapter > ch.id;
              return (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => {
                    if (validateChapter(currentChapter)) {
                      setCurrentChapter(ch.id);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className={`group relative shrink-0 flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium tracking-wide transition-all duration-300 active:scale-[0.97] ${
                    isActive
                      ? "liquid-glass-frosted-active text-[#FFFDF8] font-semibold"
                      : isPassed
                      ? "liquid-glass-frosted border-gold/30 text-text-cream hover:border-gold/50 hover:bg-white/10"
                      : "liquid-glass-frosted text-text-tertiary hover:text-text-secondary hover:border-white/20"
                  }`}
                >
                  {isPassed ? (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-gold/20 text-gold text-[10px] font-bold">
                      ✓
                    </span>
                  ) : isActive ? (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span>
                    </span>
                  ) : (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/5 text-text-tertiary text-[10px]">
                      {ch.id}
                    </span>
                  )}
                  <span>
                    Capítulo {ch.id}: <span className="opacity-90">{ch.title.split(" ")[0]}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Trilha Líquida de Progresso Integrada (Liquid Progress Bar) */}
          <div className="mt-2.5 mx-auto max-w-3xl h-[2px] w-full rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-accent via-surface-brand to-gold transition-all duration-500 ease-out shadow-[0_0_8px_rgba(212,163,115,0.4)]"
              style={{
                width: `${Math.max(10, ((currentChapter - 1) / (CHAPTERS.length - 1)) * 100)}%`,
              }}
            />
          </div>
        </div>
      </nav>

      {/* Formulário Principal */}
      <main className="mx-auto w-full max-w-3xl px-5 py-10">
        <form onSubmit={handleSubmit} noValidate>
          <div className="liquid-glass rounded-[var(--radius-control-lg)] p-6 sm:p-10">
            <div className="border-b border-border-subtle pb-5">
              <span className="type-overline text-gold">Capítulo {activeChapter.id} de {CHAPTERS.length}</span>
              <h2
                className="type-h2 mt-1 text-text-cream"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {activeChapter.title}
              </h2>
              <p className="type-body-sm mt-1 text-text-secondary">{activeChapter.subtitle}</p>
            </div>

            <div className="mt-8 flex flex-col gap-7">
              {activeChapter.id === 1 ? (
                // Capítulo 1: Campos Especiais
                <>
                  <Input
                    label="Qual seu nome completo?"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={(e) => updateRootField("name", e.target.value)}
                    error={fieldErrors.name}
                    required
                  />

                  <Input
                    label="Qual o @ do seu Instagram comercial?"
                    placeholder="@seuperfil"
                    hint="O perfil que vamos gerenciar e posicionar."
                    value={formData.instagram}
                    onChange={(e) => updateRootField("instagram", e.target.value)}
                    error={fieldErrors.instagram}
                    required
                  />

                  <Input
                    label="Qual seu melhor e-mail?"
                    type="email"
                    placeholder="seuemail@exemplo.com.br"
                    value={formData.email}
                    onChange={(e) => updateRootField("email", e.target.value)}
                    error={fieldErrors.email}
                    required
                  />

                  <Input
                    label="Qual seu WhatsApp com DDD?"
                    type="tel"
                    placeholder="(11) 99999-9999"
                    hint="Para comunicação ágil e aprovações de conteúdo."
                    value={formData.whatsapp}
                    onChange={(e) => updateRootField("whatsapp", formatWhatsapp(e.target.value))}
                    error={fieldErrors.whatsapp}
                    required
                  />

                  <div className="rounded-[var(--radius-control)] border border-border-subtle bg-surface-sunken/40 p-4">
                    <Input
                      label="Crie uma senha de acesso à plataforma (opcional)"
                      type="password"
                      placeholder="Mínimo de 8 caracteres"
                      hint="Se preenchida, você já poderá acessar sua área exclusiva para acompanhar o progresso."
                      value={formData.password}
                      onChange={(e) => updateRootField("password", e.target.value)}
                      error={fieldErrors.password}
                    />
                  </div>
                </>
              ) : (
                // Capítulos 2 a 5: Perguntas Estratégicas
                activeChapter.fields.map((f) => {
                  const val = formData.responses[f.key] || "";
                  return (
                    <div key={f.key} className="flex flex-col">
                      <TextArea
                        label={f.label}
                        hint={f.hint}
                        placeholder={f.placeholder}
                        value={val}
                        onChange={(e) => updateResponse(f.key, e.target.value)}
                        error={fieldErrors[f.key]}
                        rows={f.rows || 4}
                        required={f.required}
                      />
                      <div className="mt-1 flex justify-end">
                        <span className="type-caption text-[11px] text-text-tertiary">
                          {val.length} caracteres
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {generalError && (
              <div role="alert" className="mt-6 rounded border border-danger/30 bg-danger/10 p-4 text-center">
                <p className="type-body-sm text-danger">{generalError}</p>
              </div>
            )}

            {/* Ações e Navegação */}
            <div className="mt-10 flex items-center justify-between border-t border-border-subtle pt-6">
              {currentChapter > 1 ? (
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-text-cream liquid-glass-frosted hover:border-white/25 active:scale-[0.98] transition-all disabled:opacity-40"
                >
                  <span className="text-gold">←</span> Capítulo Anterior
                </button>
              ) : (
                <div />
              )}

              {currentChapter < CHAPTERS.length ? (
                <Button
                  type="button"
                  size="lg"
                  shape="invite"
                  onClick={handleNext}
                  className="shadow-[0_4px_20px_rgba(179,53,43,0.35)] hover:shadow-[0_6px_28px_rgba(179,53,43,0.5)] active:scale-[0.98] transition-all"
                >
                  Próximo Capítulo →
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="lg"
                  shape="invite"
                  loading={submitting}
                  className="shadow-[0_4px_24px_rgba(179,53,43,0.45)] hover:shadow-[0_8px_32px_rgba(179,53,43,0.6)] active:scale-[0.98] transition-all"
                >
                  Enviar Diagnóstico Completo
                </Button>
              )}
            </div>
          </div>
        </form>

        {/* Rodapé Elegante com Assinatura da Lara Lívia */}
        <footer className="mt-12 text-center text-text-tertiary">
          <p className="type-caption">
            Unik · Plataforma proprietária de gestão e posicionamento estratégico de conteúdo.
          </p>
          <p className="type-caption mt-1">
            Seus dados estão protegidos de ponta a ponta com criptografia e segurança Cloudflare.
          </p>
        </footer>
      </main>
      <FloatingWhatsApp phone={UNIK_WHATSAPP_PHONE} message={formData.name ? `Olá Lara! Estou preenchendo o briefing da Unik (${formData.name}) e gostaria de tirar uma dúvida.` : "Olá Lara! Estou preenchendo o briefing da Unik e gostaria de tirar uma dúvida."} label="Falar com a Lara" />

      {/* Modal Genesis Lux: Confirmação Elegante para Limpar Rascunho */}
      {isClearModalOpen && (
        <div
          className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsClearModalOpen(false);
          }}
        >
          <div
            className="relative w-full max-w-md rounded-[var(--radius-card)] border border-border-default/80 bg-[#160a0a]/98 p-6 shadow-2xl backdrop-blur-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-draft-modal-title"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-danger/15 border border-danger/30 text-danger shadow-[0_0_15px_rgba(239,68,68,0.25)]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-6 w-6"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </div>

              <div className="min-w-0 flex-1">
                <h3
                  id="clear-draft-modal-title"
                  className="type-h3 text-text-cream font-medium"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Limpar Respostas Salvas?
                </h3>
                <p className="type-body-sm mt-2 text-text-secondary leading-relaxed">
                  Esta ação apagará todas as respostas digitadas neste dispositivo e reiniciará o formulário do zero. Esta ação não poderá ser desfeita.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-white/[0.08]">
              <button
                type="button"
                onClick={() => setIsClearModalOpen(false)}
                className="rounded-full px-5 py-2.5 text-xs font-medium text-text-secondary hover:text-text-cream border border-white/10 liquid-glass-frosted active:scale-95 transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmClearDraft}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-medium text-danger bg-danger/15 border border-danger/40 hover:bg-danger/25 hover:border-danger/60 shadow-[0_0_15px_rgba(239,68,68,0.25)] active:scale-95 transition-all"
              >
                Sim, Limpar Tudo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
